import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'
import type { FilterOption } from '@/utils/documentListFilters'
import { cn } from '@/utils/cn'

type Props = {
  label: string
  placeholder: string
  value: string
  options: FilterOption[]
  onChange: (value: string) => void
  disabled?: boolean
}

export function FilterSearchDropdown({
  label,
  placeholder,
  value,
  options,
  onChange,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const active = Boolean(value)
  const shown =
    options.find((o) => o.value === value)?.label || value || placeholder

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    )
  }, [options, query])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  return (
    <div className="rfid-filter-field" ref={rootRef}>
      <span className="rfid-filter-field__label">{label}</span>
      <button
        type="button"
        className={cn(
          'rfid-filter-dropdown__trigger',
          active && 'rfid-filter-dropdown__trigger--active',
          disabled && 'rfid-filter-dropdown__trigger--disabled'
        )}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span
          className={cn(
            'rfid-filter-dropdown__value',
            !active && 'rfid-filter-dropdown__value--placeholder'
          )}
        >
          {shown}
        </span>
        <span className="rfid-filter-dropdown__icons">
          {active ? (
            <span
              role="button"
              tabIndex={0}
              className="rfid-filter-dropdown__clear-icon"
              aria-label={`Clear ${label}`}
              onClick={(e) => {
                e.stopPropagation()
                onChange('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange('')
                }
              }}
            >
              <X size={14} strokeWidth={2.5} />
            </span>
          ) : null}
          <ChevronDown size={15} strokeWidth={2.4} />
        </span>
      </button>

      {open ? (
        <div className="rfid-filter-dropdown__panel" role="listbox">
          <div className="rfid-filter-dropdown__search">
            <Search size={14} strokeWidth={2.4} />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}…`}
              aria-label={`Search ${label}`}
            />
          </div>
          <div className="rfid-filter-dropdown__list">
            {filtered.length === 0 ? (
              <div className="rfid-filter-dropdown__empty">No matches</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  className={cn(
                    'rfid-filter-dropdown__option',
                    o.value === value && 'rfid-filter-dropdown__option--selected'
                  )}
                  onClick={() => {
                    onChange(o.value)
                    setOpen(false)
                    setQuery('')
                  }}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
